import { fiscalYearLabel } from "@/features/purchase/constants";
import type { GstSupplyMode, TaxRate, TaxType } from "@/features/settings/types";

export const TAX_TYPES: { value: TaxType; label: string }[] = [
  { value: "cgst", label: "CGST" },
  { value: "sgst", label: "SGST" },
  { value: "igst", label: "IGST" },
  { value: "cess", label: "Cess" },
  { value: "gst", label: "GST (legacy combined)" },
  { value: "other", label: "Other" },
];

/** Common GST slabs used when creating rates or picking on invoices */
export const GST_SLABS = [0, 5, 12, 18, 28] as const;

export const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
] as const;

/** Example doc number using current FY rules */
export function previewDocNumber(prefix: string, startMonth = 4) {
  const fy = fiscalYearLabel(new Date(), startMonth);
  const pfx = (prefix || "XX").trim().toUpperCase() || "XX";
  return `${pfx}-${fy}-0001`;
}

export function labelFor<T extends string>(
  options: { value: T; label: string }[],
  value: T | string | null | undefined,
) {
  if (!value) return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

function normalizeState(value: string | null | undefined) {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Intra-state (same place of supply as company) → CGST + SGST.
 * Inter-state → IGST.
 */
export function resolveGstSupplyMode(
  companyState: string | null | undefined,
  clientPlaceOfSupply: string | null | undefined,
): GstSupplyMode {
  const a = normalizeState(companyState);
  const b = normalizeState(clientPlaceOfSupply);
  if (!a || !b) return "intra_state";
  return a === b ? "intra_state" : "inter_state";
}

/**
 * Pick tax components for a GST slab based on client place of supply vs company state.
 * Used when raising invoices / quotations — select slab (e.g. 18%), then apply these rates.
 */
export function resolveApplicableTaxes(args: {
  rates: TaxRate[];
  gstSlab: number;
  companyState?: string | null;
  clientPlaceOfSupply?: string | null;
  asOf?: Date | string;
}): { mode: GstSupplyMode; taxes: TaxRate[]; totalRate: number } {
  const asOf =
    typeof args.asOf === "string"
      ? args.asOf.slice(0, 10)
      : (args.asOf ?? new Date()).toISOString().slice(0, 10);

  const mode = resolveGstSupplyMode(args.companyState, args.clientPlaceOfSupply);
  const wanted: TaxType[] = mode === "intra_state" ? ["cgst", "sgst"] : ["igst"];

  const taxes = args.rates.filter((r) => {
    if (!r.is_active) return false;
    if (Number(r.gst_slab) !== Number(args.gstSlab)) return false;
    if (!wanted.includes(r.tax_type)) return false;
    if (r.effective_from > asOf) return false;
    if (r.effective_to && r.effective_to < asOf) return false;
    return true;
  });

  const totalRate = taxes.reduce((s, t) => s + t.rate, 0);
  return { mode, taxes, totalRate };
}

/** Unique active GST slabs available in the catalogue (for invoice slab dropdown). */
export function listGstSlabs(rates: TaxRate[]): number[] {
  const set = new Set<number>();
  for (const r of rates) {
    if (!r.is_active || r.gst_slab === null || r.gst_slab === undefined) continue;
    if (r.tax_type === "cgst" || r.tax_type === "sgst" || r.tax_type === "igst") {
      set.add(Number(r.gst_slab));
    }
  }
  return [...set].sort((a, b) => a - b);
}
